import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/DataTable/DataTable';
import DetailDrawer from '@/components/DetailDrawer/DetailDrawer';
import GuidancePanel, { GUIDANCE } from '@/components/GuidancePanel';
import { VENDOR_LABELS, type DriftStatus, type ScenarioStatus, type VendorId } from '@/types/domain';
import type { ScenarioId } from '@/types/ids';

import { createFetchScenariosApi } from './api.fetch';
import type { ScenarioRow, ScenariosApi, ScenariosQuery } from './api';
import './Scenarios.css';

interface ScenariosProps {
  api?: ScenariosApi;
}

const DEFAULT_API: ScenariosApi = createFetchScenariosApi();

const STATUS_FILTERS: Array<{ key: string; label: string; status?: ScenarioStatus }> = [
  { key: 'all', label: 'All' },
  { key: 'PUBLISHED', label: 'Published', status: 'PUBLISHED' },
  { key: 'DRAFT', label: 'Draft', status: 'DRAFT' },
  { key: 'RETIRED', label: 'Retired', status: 'RETIRED' },
];

const FAMILY_CARDS: Array<{ label: string; value: string; coverage: string }> = [
  { label: 'AML', value: '412', coverage: 'Coverage 94%' },
  { label: 'Trade', value: '186', coverage: 'Coverage 81%' },
  { label: 'Sanctions', value: '217', coverage: 'Coverage 88%' },
  { label: 'Mule', value: '148', coverage: 'Coverage 76%' },
  { label: 'Fraud', value: '172', coverage: 'Coverage 62%' },
  { label: 'KYC', value: '112', coverage: 'Coverage 69%' },
];

interface EditDraft {
  row: ScenarioRow;
  name: string;
  owner: string;
  status: ScenarioStatus;
}

interface NewDraft {
  name: string;
  owner: string;
  family: string;
  sourcePlatform: VendorId;
  sourceScenarioId: string;
  status: ScenarioStatus;
}

const EMPTY_NEW: NewDraft = {
  name: '',
  owner: '',
  family: 'AML',
  sourcePlatform: 'Fccm',
  sourceScenarioId: '',
  status: 'DRAFT',
};

export default function Scenarios({ api = DEFAULT_API }: ScenariosProps) {
  const [statusFilter, setStatusFilter] = useState<ScenarioStatus | undefined>(undefined);
  const [platformFilter, setPlatformFilter] = useState<VendorId | undefined>(undefined);
  const [selected, setSelected] = useState<ScenarioRow | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [edit, setEdit] = useState<EditDraft | null>(null);
  const [toDelete, setToDelete] = useState<ScenarioRow | null>(null);
  const [toast, setToast] = useState<string>('');

  // Client-side mutations (optimistic). Backend PATCH/POST/DELETE on
  // /api/v1/scenarios is a follow-up; the server returns the canonical
  // catalogue and we compose local deltas on top so every action is
  // observable immediately.
  const [extras, setExtras] = useState<ScenarioRow[]>([]);
  const [patches, setPatches] = useState<Record<string, Partial<ScenarioRow>>>({});
  const [tombstones, setTombstones] = useState<Set<string>>(new Set());

  const query: ScenariosQuery = useMemo(
    () => ({
      ...(statusFilter && { status: statusFilter }),
      ...(platformFilter && { sourcePlatform: platformFilter }),
    }),
    [statusFilter, platformFilter],
  );

  const scenariosQ = useQuery({
    queryKey: ['scenarios', 'list', query],
    queryFn: () => api.listScenarios(query),
    staleTime: 60_000,
  });

  const rows: ScenarioRow[] = useMemo(() => {
    const server = scenariosQ.data ?? [];
    const merged: ScenarioRow[] = [];
    for (const r of server) {
      const id = r.scenarioId as unknown as string;
      if (tombstones.has(id)) continue;
      const patch = patches[id];
      merged.push(patch ? { ...r, ...patch } : r);
    }
    return [...extras, ...merged];
  }, [scenariosQ.data, extras, patches, tombstones]);

  const flashToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 3500);
  };

  const commitEdit = () => {
    if (!edit) return;
    const id = edit.row.scenarioId as unknown as string;
    setPatches((p) => ({
      ...p,
      [id]: { name: edit.name, owner: edit.owner, status: edit.status },
    }));
    flashToast(`Scenario "${edit.name}" updated.`);
    setEdit(null);
  };

  const commitDelete = () => {
    if (!toDelete) return;
    const id = toDelete.scenarioId as unknown as string;
    setTombstones((t) => {
      const next = new Set(t);
      next.add(id);
      return next;
    });
    flashToast(`Scenario "${toDelete.name}" deleted.`);
    setToDelete(null);
  };

  const commitNew = (draft: NewDraft) => {
    const id = `local-${Date.now().toString(36)}`;
    const row: ScenarioRow = {
      scenarioId: id as unknown as ScenarioId,
      name: draft.name,
      sourcePlatform: draft.sourcePlatform,
      sourceScenarioId: draft.sourceScenarioId || `LOCAL-${id.slice(-4).toUpperCase()}`,
      latestVersion: 'v1',
      status: draft.status,
      lastReplayAt: null,
      driftStatus: 'NONE',
      owner: draft.owner,
      family: draft.family,
    };
    setExtras((e) => [row, ...e]);
    flashToast(`Scenario "${draft.name}" created.`);
    setNewOpen(false);
  };

  const columns = useMemo<ColumnDef<ScenarioRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Scenario',
        cell: (ctx) => (
          <div className="scenario-name-cell">
            <strong>{ctx.row.original.name}</strong>
            <div className="scenario-name-cell__family">{ctx.row.original.family}</div>
          </div>
        ),
      },
      {
        id: 'sourcePlatform',
        header: 'Source platform',
        cell: (ctx) => <span>{VENDOR_LABELS[ctx.row.original.sourcePlatform]}</span>,
      },
      {
        id: 'sourceScenarioId',
        header: 'Source scenario id',
        cell: (ctx) => <span className="mono">{ctx.row.original.sourceScenarioId}</span>,
      },
      {
        id: 'latestVersion',
        header: 'Latest version',
        cell: (ctx) => <span className="mono">{ctx.row.original.latestVersion}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (ctx) => (
          <span className={statusChipClass(ctx.row.original.status)}>
            <span className="dot" />
            {ctx.row.original.status}
          </span>
        ),
      },
      {
        id: 'lastReplayAt',
        header: 'Last replay date',
        cell: (ctx) => (
          <span className="mono" style={{ color: 'var(--slate-500)' }}>
            {formatRelative(ctx.row.original.lastReplayAt)}
          </span>
        ),
      },
      {
        id: 'driftStatus',
        header: 'Drift status',
        cell: (ctx) => renderDriftStatus(ctx.row.original.driftStatus),
      },
      {
        id: 'owner',
        header: 'Owner',
        cell: (ctx) => <span>{ctx.row.original.owner}</span>,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (ctx) => (
          <div className="scenarios-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                const row = ctx.row.original;
                setEdit({ row, name: row.name, owner: row.owner, status: row.status });
              }}
              data-testid={`scenario-edit-${ctx.row.original.scenarioId as unknown as string}`}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-danger"
              onClick={(e) => {
                e.stopPropagation();
                setToDelete(ctx.row.original);
              }}
              data-testid={`scenario-delete-${ctx.row.original.scenarioId as unknown as string}`}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHead onNew={() => setNewOpen(true)} />

      <GuidancePanel screenKey="scenarios" {...GUIDANCE.scenarios} />

      <div className="scenarios-family-grid">
        {FAMILY_CARDS.map((card) => (
          <div key={card.label} className="card">
            <div className="eyebrow">{card.label}</div>
            <div className="serif">{card.value}</div>
            <div className="scenarios-family-coverage">{card.coverage}</div>
          </div>
        ))}
      </div>

      <section className="card">
        <header className="card-head">
          <div className="eyebrow">SCENARIO CATALOG · {rows.length}</div>
          <div className="tab-pill-row">
            {STATUS_FILTERS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`tab-pill${(opt.status ?? undefined) === statusFilter ? ' active' : ''}`}
                onClick={() => setStatusFilter(opt.status)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </header>
        <div className="scenarios-filter-row">
          <label>
            Source platform
            <select
              aria-label="Filter by source platform"
              title="Narrow the catalogue to scenarios ported from a single upstream engine (Oracle FCCM, SAS AML, Actimize, etc.)."
              value={platformFilter ?? ''}
              onChange={(e) => setPlatformFilter((e.target.value || undefined) as VendorId | undefined)}
            >
              <option value="">All platforms</option>
              {(Object.keys(VENDOR_LABELS) as VendorId[]).map((v) => (
                <option key={v} value={v}>
                  {VENDOR_LABELS[v]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              aria-label="Filter by status"
              value={statusFilter ?? ''}
              onChange={(e) =>
                setStatusFilter((e.target.value || undefined) as ScenarioStatus | undefined)
              }
            >
              <option value="">Any status</option>
              <option value="PUBLISHED">Published</option>
              <option value="DRAFT">Draft</option>
              <option value="RETIRED">Retired</option>
            </select>
          </label>
        </div>
        <DataTable
          data={rows}
          columns={columns}
          ariaLabel="Scenario catalog"
          caption="Scenario catalog"
          testId="scenarios-table"
          loading={scenariosQ.isLoading}
          error={scenariosQ.error}
          emptyMessage="No scenarios match the current filters."
          getRowId={(row) => row.scenarioId as string}
          onRowClick={(row) => setSelected(row.original)}
        />
      </section>

      {selected && (
        <DetailDrawer
          testId="scenarios-drawer"
          title={selected.name}
          subtitle={`${VENDOR_LABELS[selected.sourcePlatform]} · ${selected.sourceScenarioId}`}
          payload={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {newOpen && (
        <NewScenarioModal
          onCancel={() => setNewOpen(false)}
          onSubmit={commitNew}
        />
      )}

      {edit && (
        <EditScenarioModal
          draft={edit}
          onCancel={() => setEdit(null)}
          onChange={setEdit}
          onSubmit={commitEdit}
        />
      )}

      {toDelete && (
        <ConfirmDeleteModal
          row={toDelete}
          onCancel={() => setToDelete(null)}
          onConfirm={commitDelete}
        />
      )}

      {toast && (
        <div className="scenarios-toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
}

function PageHead({ onNew }: { onNew: () => void }) {
  return (
    <div className="page-head">
      <div>
        <span className="eyebrow">§ SCENARIO LIBRARY</span>
        <h1>Scenario library</h1>
        <p style={{ fontSize: 14, color: 'var(--slate-500)', margin: '8px 0 0', maxWidth: 620 }}>
          Versioned typologies curated by operators and regulators. Click a row to inspect;
          use the per-row buttons to edit or retire a scenario.
        </p>
      </div>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onNew}
        data-testid="scenarios-new"
      >
        + New scenario
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

function NewScenarioModal({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (draft: NewDraft) => void;
}) {
  const [draft, setDraft] = useState<NewDraft>(EMPTY_NEW);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const step1Valid = draft.name.trim().length > 2 && draft.family.length > 0;
  const step2Valid = draft.sourcePlatform.length > 0;
  const step3Valid = draft.owner.trim().length > 0;
  const canFinish = step1Valid && step2Valid && step3Valid;
  const update = (next: Partial<NewDraft>) => setDraft((d) => ({ ...d, ...next }));

  return (
    <ModalBackdrop onCancel={onCancel} testId="scenario-new-modal" title="New scenario">
      <div className="replays-wizard-steps" role="list">
        {([1, 2, 3] as const).map((n) => (
          <div
            key={n}
            role="listitem"
            className={`replays-wizard-step${step === n ? ' active' : ''}${step > n ? ' done' : ''}`}
          >
            <span className="replays-wizard-step__num">{n}</span>
            <span className="replays-wizard-step__label">
              {n === 1 ? 'Identity' : n === 2 ? 'Source' : 'Review'}
            </span>
          </div>
        ))}
      </div>

      <form
        className="scenarios-modal-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canFinish) onSubmit(draft);
        }}
      >
        {step === 1 && (
          <>
            <p className="replays-wizard-hint">
              <strong>Identity &amp; family.</strong> The canonical name is how analysts discover
              the scenario in the catalogue. The family groups it under one of the six BRD §24.1
              typology buckets so coverage metrics aggregate cleanly.
            </p>
            <label>
              Name
              <input
                required
                autoFocus
                value={draft.name}
                onChange={(e) => update({ name: e.target.value })}
                data-testid="scenario-new-name"
                placeholder="e.g. structuring.v8"
              />
            </label>
            <label>
              Family
              <select
                value={draft.family}
                onChange={(e) => update({ family: e.target.value })}
                aria-label="Family"
              >
                {FAMILY_CARDS.map((f) => (
                  <option key={f.label} value={f.label}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {step === 2 && (
          <>
            <p className="replays-wizard-hint">
              <strong>Source platform &amp; vendor id.</strong> Pin the upstream vendor platform
              the scenario will be compared against. Leave source id blank for scenarios that are
              authored natively in Sentinel rather than ported.
            </p>
            <label>
              Source platform
              <select
                value={draft.sourcePlatform}
                onChange={(e) => update({ sourcePlatform: e.target.value as VendorId })}
                aria-label="Source platform"
              >
                {(Object.keys(VENDOR_LABELS) as VendorId[]).map((v) => (
                  <option key={v} value={v}>
                    {VENDOR_LABELS[v]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Source scenario id
              <input
                value={draft.sourceScenarioId}
                placeholder="e.g. KDDSCN-2000-01 (optional)"
                onChange={(e) => update({ sourceScenarioId: e.target.value })}
              />
            </label>
          </>
        )}

        {step === 3 && (
          <>
            <p className="replays-wizard-hint">
              <strong>Owner &amp; review.</strong> The owner receives drift notifications and
              drives the sign-off workflow. Draft scenarios are editable; Published ones are
              immutable until a new version is cut.
            </p>
            <label>
              Owner
              <input
                required
                value={draft.owner}
                onChange={(e) => update({ owner: e.target.value })}
                data-testid="scenario-new-owner"
                placeholder="e.g. Elena Vasquez"
              />
            </label>
            <label>
              Status
              <select
                value={draft.status}
                onChange={(e) => update({ status: e.target.value as ScenarioStatus })}
              >
                <option value="DRAFT">Draft</option>
                <option value="PUBLISHED">Published</option>
                <option value="RETIRED">Retired</option>
              </select>
            </label>
            <div className="new-run-field">
              <label>Review</label>
              <div className="new-run-preview">
                {[
                  `name:            ${draft.name || '—'}`,
                  `family:          ${draft.family}`,
                  `sourcePlatform:  ${VENDOR_LABELS[draft.sourcePlatform]}`,
                  `sourceId:        ${draft.sourceScenarioId || '(native)'}`,
                  `owner:           ${draft.owner || '—'}`,
                  `status:          ${draft.status}`,
                ].join('\n')}
              </div>
            </div>
          </>
        )}

        <div className="scenarios-modal-footer replays-wizard-footer">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <span style={{ flex: 1 }} />
          {step > 1 && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setStep((s) => (s === 3 ? 2 : 1))}
              data-testid="scenario-new-back"
            >
              Back
            </button>
          )}
          {step < 3 ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={step === 1 ? !step1Valid : !step2Valid}
              onClick={() => setStep((s) => (s === 1 ? 2 : 3))}
              data-testid="scenario-new-next"
            >
              Next
            </button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={!canFinish}>
              Create scenario
            </button>
          )}
        </div>
      </form>
    </ModalBackdrop>
  );
}

function EditScenarioModal({
  draft,
  onCancel,
  onChange,
  onSubmit,
}: {
  draft: EditDraft;
  onCancel: () => void;
  onChange: (next: EditDraft) => void;
  onSubmit: () => void;
}) {
  const valid = draft.name.trim().length > 2 && draft.owner.trim().length > 0;
  return (
    <ModalBackdrop
      onCancel={onCancel}
      testId="scenario-edit-modal"
      title={`Edit ${draft.row.name}`}
    >
      <form
        className="scenarios-modal-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onSubmit();
        }}
      >
        <label>
          Name
          <input
            required
            autoFocus
            value={draft.name}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            data-testid="scenario-edit-name"
          />
        </label>
        <label>
          Owner
          <input
            required
            value={draft.owner}
            onChange={(e) => onChange({ ...draft, owner: e.target.value })}
          />
        </label>
        <label>
          Status
          <select
            value={draft.status}
            onChange={(e) =>
              onChange({ ...draft, status: e.target.value as ScenarioStatus })
            }
          >
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
            <option value="RETIRED">Retired</option>
          </select>
        </label>
        <ModalFooter onCancel={onCancel} submitLabel="Save changes" disabled={!valid} />
      </form>
    </ModalBackdrop>
  );
}

function ConfirmDeleteModal({
  row,
  onCancel,
  onConfirm,
}: {
  row: ScenarioRow;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ModalBackdrop onCancel={onCancel} testId="scenario-delete-modal" title="Delete scenario">
      <div className="scenarios-modal-body">
        <p>
          Delete <strong>{row.name}</strong>? This removes the scenario from the live
          catalogue. Versioned history is preserved for audit and cannot be undone from the UI.
        </p>
      </div>
      <div className="scenarios-modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary btn-danger"
          onClick={onConfirm}
          data-testid="scenario-delete-confirm"
        >
          Delete scenario
        </button>
      </div>
    </ModalBackdrop>
  );
}

function ModalBackdrop({
  onCancel,
  title,
  testId,
  children,
}: {
  onCancel: () => void;
  title: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="scenarios-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId}
      onClick={onCancel}
    >
      <div className="scenarios-modal" onClick={(e) => e.stopPropagation()}>
        <header className="scenarios-modal-head">
          <div className="eyebrow">§ {title.toUpperCase()}</div>
          <h2 className="serif" style={{ margin: '6px 0 0', fontSize: 22 }}>
            {title}
          </h2>
        </header>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel,
  submitLabel,
  disabled,
}: {
  onCancel: () => void;
  submitLabel: string;
  disabled: boolean;
}) {
  return (
    <div className="scenarios-modal-footer">
      <button type="button" className="btn btn-ghost" onClick={onCancel}>
        Cancel
      </button>
      <button type="submit" className="btn btn-primary" disabled={disabled}>
        {submitLabel}
      </button>
    </div>
  );
}

function statusChipClass(status: ScenarioStatus): string {
  switch (status) {
    case 'DRAFT':
      return 'chip warn';
    case 'RETIRED':
      return 'chip fail';
    case 'PUBLISHED':
    default:
      return 'chip';
  }
}

function renderDriftStatus(drift: DriftStatus) {
  if (drift === 'NONE') {
    return <span className="drift-chip-none">—</span>;
  }
  const cls = drift === 'OPEN' ? 'chip fail' : drift === 'REVIEW' ? 'chip warn' : 'chip info';
  return (
    <span className={cls}>
      <span className="dot" />
      {drift}
    </span>
  );
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Date.now() - then;
  if (diff < 0) return 'just now';
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < hour) {
    const mins = Math.max(1, Math.round(diff / minute));
    return `${mins} min ago`;
  }
  if (diff < day) return `${Math.round(diff / hour)}h ago`;
  return `${Math.round(diff / day)}d ago`;
}
